import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, Clock, Radio, CheckCircle2, Wallet } from "lucide-react";
import { StatCard, StatusBadge, PaymentBadge, Loading, EmptyState, Money } from "@/components/common";
import { formatDate, formatCurrency } from "@/lib/format";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats(false)); }, []);

  if (stats === null) return <Loading />;
  if (!stats) return <EmptyState title="Unable to load dashboard" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Overview of orders, campaigns and payments.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="stat-active-orders" icon={Activity} tone="blue" label="Active Orders" value={stats.active_orders} sub="Confirmed / In Progress / Running" />
        <StatCard testid="stat-pending-orders" icon={Clock} tone="amber" label="Pending Orders" value={stats.pending_orders} sub="Awaiting confirmation" />
        <StatCard testid="stat-running-campaigns" icon={Radio} tone="sky" label="Running Campaigns" value={stats.running_campaigns} sub="Live now" />
        <StatCard testid="stat-completed-orders" icon={CheckCircle2} tone="emerald" label="Completed Orders" value={stats.completed_orders} sub="Delivered" />
        <StatCard testid="stat-pending-payments" icon={Wallet} tone="rose" label="Pending Payments" value={formatCurrency(stats.pending_payments_amount)} sub={`${stats.pending_payments_count} orders`} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900 font-display">Recent Orders</h2>
        </div>
        {stats.recent_orders.length === 0 ? (
          <EmptyState icon={Activity} title="No orders yet" sub="Create your first order to get started." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Staff</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_orders.map((o) => (
                  <TableRow key={o.order_id} data-testid={`recent-order-${o.order_id}`}
                    className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/${o.order_id}`)}>
                    <TableCell className="font-mono font-medium text-slate-900">{o.order_id}</TableCell>
                    <TableCell>{o.business_name}</TableCell>
                    <TableCell className="text-slate-600">{o.plan_name || "—"}</TableCell>
                    <TableCell className="text-slate-600">{formatDate(o.start_date)}</TableCell>
                    <TableCell className="text-slate-600">{formatDate(o.end_date)}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell><PaymentBadge status={o.payment_status} /></TableCell>
                    <TableCell className="text-slate-600">{o.assigned_staff_name || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
